import React, { lazy, Suspense, useEffect } from "react";
import { HashRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import { Helmet } from "react-helmet";
import DashboardLayout from "components/layouts/DashboardLayout";
import { useTranslation } from "react-i18next";
import { dir } from "i18next";
import { ConfigProvider } from 'antd';
import fa_IR from 'antd/lib/locale/fa_IR';

// Lazy-loaded components for each route
const AccountPage = lazy(() => import("pages/account/AccountPage"));
const ShareDevicePage = lazy(() => import("pages/devices/ShareDevicePage"));
const LoginPage = lazy(() => import("pages/authentication/Login"));
const RegisterPage = lazy(() => import("pages/authentication/Register"));
const SigninPage = lazy(() => import("pages/authentication/Signin"));
const LoadingPage = lazy(() => import("pages/authentication/Loading"));
const FaqPage = lazy(() => import("pages/faq/FaqPage"));
const Privacy = lazy(() => import("pages/privacy/privacy"));
const DevicesPage = lazy(() => import("pages/devices/DevicesPage"));
const OrganizationDetails = lazy(() => import("pages/organizationDetials/OrganizationDetails"));
const Policies = lazy(() => import("pages/privacy&policy/PrivacyAndPolicies"));
const DataDeletion = lazy(() => import("pages/dataDeletion/DataDeletionInstructions"));
const OrganizationAdminSignupForm = lazy(() => import("pages/organizations/OrganizationAdminSignUpForm"));
const OrganizationsPage = lazy(() => import("pages/organizations/OrganizationsPage"));
const FirmwareInitPage = lazy(() => import("pages/firmwareInit/FirmwareInitPage"));
const SubscriptionPage = lazy(() => import("pages/subscriptions/SubscriptionPage"));
const UpgradeHardwarePage = lazy(() => import("pages/upgradeHardware/UpgradeHardwarePage"));
const UsersManagementPage = lazy(() => import("pages/usersManagement/UsersManagementPage"));
const AdminAnalyticsPage = lazy(() => import("pages/adminAnalytics/AdminAnalyticsPage"));

const App = () => {
	// Translation hook
	const { i18n, t } = useTranslation();

	// Set the direction and language attributes when the language changes
	useEffect(() => {
		document.body.classList.add(`direction-${dir(i18n.language)}`);
		document.body.dataset.direction = dir(i18n.language);
		document.querySelector("html").lang = i18n.language;
	}, [i18n.language]);

	return (
		<ConfigProvider direction={dir(i18n.language)} locale={i18n.language === 'fa' ? fa_IR : null}>
			<Router>
				<Suspense fallback={<div className="margin-auto padding-3">{t('LOADING')}...</div>}>
					{/* Use Helmet for setting default metadata */}
					<Helmet>
						<title>{t("app.defaultTitle")}</title>
						<meta name="description" content={t("app.defaultDescription")} />
					</Helmet>
					<Switch>
						<Route exact path="/login" component={() => <PageWithHelmet titleKey="login.title" Component={LoginPage} />} />
						<Route exact path="/register" component={() => <PageWithHelmet titleKey="register.title" Component={RegisterPage} />} />
						<Route exact path="/signin" component={() => <PageWithHelmet titleKey="signin.title" Component={SigninPage} />} />
						<Route exact path="/loading" component={() => <PageWithHelmet titleKey="loading.title" Component={LoadingPage} />} />
						<Route exact path="/firmwareInit" component={() => <PageWithHelmet titleKey="firmwareInit.title" Component={FirmwareInitPage} />} />
						<Route exact path="/privacy_policy" component={() => <PageWithHelmet titleKey="policies.title" Component={Policies} />} />
						<Route exact path="/data-deletion-instructions" component={() => <PageWithHelmet titleKey="dataDeletion.title" Component={DataDeletion} />} />
						<Route exact path="/organization-admin-signup" component={() => <PageWithHelmet titleKey="organizationAdminSignup" Component={OrganizationAdminSignupForm} />} />

						{/* Dashboard Layout with nested routes */}
						<DashboardLayout>
							<Switch>
								<Route exact path="/faq" component={() => <PageWithHelmet titleKey="faq.title" Component={FaqPage} />} />
								<Route exact path="/privacy" component={() => <PageWithHelmet titleKey="privacy.title" Component={Privacy} />} />
								<Route exact path="/account_deletion" component={() => <PageWithHelmet titleKey="accountDeletion.title" Component={FaqPage} />} />
								<Route exact path="/devices" component={() => <PageWithHelmet titleKey="devices.title" Component={DevicesPage} />} />
								<Route exact path="/organizations" component={() => <PageWithHelmet titleKey="organizations.title" Component={OrganizationsPage} />} />
								<Route exact path="/subscriptions" component={() => <PageWithHelmet titleKey="subscriptions.title" Component={SubscriptionPage} />} />
								<Route exact path="/organizationDetails" component={() => <PageWithHelmet titleKey="organizationDetails.title" Component={OrganizationDetails} />} />
								<Route exact path="/account" component={() => <PageWithHelmet titleKey="account.title" Component={AccountPage} />} />
								<Route exact path="/devices/share" component={() => <PageWithHelmet titleKey="shareDevice.title" Component={ShareDevicePage} />} />
								<Route exact path="/upgrade-hardware" component={() => <PageWithHelmet titleKey="upgradeHardware.title" Component={UpgradeHardwarePage} />} />
								<Route exact path="/users-management" component={() => <PageWithHelmet titleKey="usersManagement.title" Component={UsersManagementPage} />} />
								<Route exact path="/admin-analytics" component={() => <PageWithHelmet titleKey="analytics.title" Component={AdminAnalyticsPage} />} />

								{/* Redirect to devices as a fallback */}
								<Redirect to="/devices" />
							</Switch>
						</DashboardLayout>
					</Switch>
				</Suspense>
			</Router>
		</ConfigProvider>
	);
};

export default App;

// Helper component to wrap pages with Helmet
const PageWithHelmet = ({ titleKey, Component }) => {
	const { t } = useTranslation();
	return (
		<>
			<Helmet>
				<title>{t(titleKey)}</title>
			</Helmet>
			<Component />
		</>
	);
};
